import { mutate } from 'swr';
import { deleteAPI, postAPI } from '~/lib/api';
import { useUser } from './useUser';
import { useUserAction } from './useUserAction';
import { useRouter } from 'next/navigation';
import { useState, useCallback } from 'react';
import type {
  Database,
  NestedHome,
  NestedProject,
  NestedViewing,
} from '@habity/types';
import { useTracking } from './useTracking';
import { useAgency } from './useAgency';
import {
  EVENTS,
  VIEWING_ERROR_CONFIG,
  ViewingParticipantErrorCode,
} from '@habity/constants';
import { revalidateHomeViewing } from '~/app/actions/revalidateViewing';
import { getViewingToastMessage, useToast } from '~/lib/toasts';
import { Icon } from '@chakra-ui/react';
import { ICONS } from '~/constants';
import { createClient } from '~/lib/providers/supabase/client';
import { deleteViewingParticipant } from '~/lib/providers/supabase/viewing_participants';
import { getHomePath, getProjectPath } from '@habity/services';
import { revalidateProjectViewing } from '~/app/actions/revalidateProjectViewing';
import { getProjectViewingToastMessage } from '~/lib/toasts';
type ViewingParticipant =
  Database['public']['Tables']['viewing_participants']['Row'];

export const useViewing = () => {
  const { user } = useUser();
  const { executeAction, canExecuteImmediately } = useUserAction();
  const router = useRouter();
  const { track } = useTracking();
  const { agency } = useAgency();
  const { showToast } = useToast();

  // Local cache of viewing registration status
  const [viewingRegistrationCache, setViewingRegistrationCache] = useState<
    Record<number, boolean>
  >({});

  /**
   * Add the current user to a viewing's participants
   * @param home The home associated with the viewing (optional for project viewings)
   * @param viewing The viewing to add the user to
   * @param slotId Required slot ID for the viewing
   * @param project Optional project for project viewings (affects toast, tracking, revalidation)
   * @returns A promise that resolves to an object with success status and optimistic update info
   */
  const addToViewing = async (
    home: NestedHome | null,
    viewing: NestedViewing,
    slotId: number,
    project?: NestedProject
  ) => {
    const homeId = home?.id;
    // If user is not logged in OR doesn't have a complete profile, use executeAction
    // which will open the enhanced login modal at the appropriate step
    if (!canExecuteImmediately) {
      await executeAction({
        home: home || undefined,
        project,
        action: 'booked_viewing',
        viewingSlotId: slotId,
        redirectTo: project
          ? getProjectPath(project)
          : home
          ? getHomePath(home)
          : '/',
      });
      return { success: false, needsAuth: true };
    }

    // At this point, user is guaranteed to exist and have a complete profile
    const userId = user!.id;

    // Create the viewing participant data
    const viewingParticipantData: Database['public']['Tables']['viewing_participants']['Insert'] =
      {
        user_id: userId,
        viewing_slot_id: slotId,
        origin: 'internal',
      };

    // Prepare optimistic update info
    const optimisticUpdate = {
      viewing,
      isRegistered: true,
      user,
    };

    // Real-time availability check: Fetch fresh data from Supabase before booking
    // This prevents unnecessary backend calls when slot is already full
    const supabase = createClient();
    const { data: freshSlot, error: slotError } = await supabase
      .from('viewing_slots')
      .select(
        `
        id,
        viewing_participants (
          user_id
        )
      `
      )
      .eq('id', slotId)
      .single();

    if (slotError) {
      console.error('Error fetching slot availability:', slotError);
      // Continue with booking attempt - backend will validate
    } else if (freshSlot) {
      const maxParticipants = viewing.participants_per_slot || 0;
      const currentParticipants = freshSlot.viewing_participants?.length || 0;
      const availableSeats = maxParticipants - currentParticipants;

      if (availableSeats <= 0) {
        // Slot is full - show error and abort (don't call backend)
        showToast(
          {
            title: 'Visningstillfället är fullbokat',
            description:
              'Detta visningstillfälle är fullbokad, försök med en annan tid.',
          },
          { status: 'warning' }
        );
        return { success: false }; // Return early - no backend call made
      }
    }

    // Update local cache
    setViewingRegistrationCache((prev) => ({
      ...prev,
      [viewing.id]: true,
    }));

    try {
      // Call backend API which handles both VITEC sync AND Supabase insert
      // The backend only inserts into Supabase if VITEC succeeds
      const response = await postAPI(
        '/providers/supabase/viewing_participants',
        JSON.stringify(viewingParticipantData)
      );

      // Check for viewing-specific errors from VITEC
      if (response?.errorCode) {
        // Revert optimistic update
        setViewingRegistrationCache((prev) => ({
          ...prev,
          [viewing.id]: false,
        }));

        // Get error configuration based on error code
        const errorCode = response.errorCode as ViewingParticipantErrorCode;
        const errorConfig =
          VIEWING_ERROR_CONFIG[errorCode] ||
          VIEWING_ERROR_CONFIG[ViewingParticipantErrorCode.UNKNOWN_ERROR];

        // Use VITEC's error message if available, otherwise use config description
        const errorMessage = response.errorMessage || errorConfig.description;

        showToast(
          { title: errorConfig.title, description: errorMessage },
          { status: errorConfig.status }
        );
        return { success: false, optimisticUpdate, error: errorMessage };
      }

      // Revalidate the page cache to get fresh viewing data
      if (project) {
        await revalidateProjectViewing(project);
      } else if (home) {
        await revalidateHomeViewing(home);
      }

      // Refresh the user data to update the UI
      mutate(['users', userId]);

      // Refresh the page to update all components immediately
      router?.refresh();

      // Track viewing registration event
      track(EVENTS.BOOKED_VIEWING, {
        viewingId: viewing.id,
        homeId: homeId,
        projectId: project?.id,
        userId: userId,
        agencyId: agency?.id,
      });

      // Show success toast
      const message = project
        ? getProjectViewingToastMessage(project)
        : home
        ? getViewingToastMessage(home)
        : { title: 'Visning bokad', description: '' };
      showToast(message, {
        icon: <Icon as={ICONS.CALENDAR_CHECK} boxSize={6} />,
      });

      return { success: true, optimisticUpdate };
    } catch (error) {
      console.error('Error adding to viewing:', error);

      // Revert optimistic update
      setViewingRegistrationCache((prev) => ({
        ...prev,
        [viewing.id]: false,
      }));

      // Show user-friendly error message
      showToast(
        {
          title: 'Kunde inte boka visning',
          description: 'Ett fel uppstod. Försök igen senare.',
        },
        { status: 'error' }
      );

      return {
        success: false,
        optimisticUpdate,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  };

  /**
   * Remove the current user from a viewing's participants
   * @param home The home associated with the viewing (optional for project viewings)
   * @param viewing The viewing to remove the user from
   * @param project Optional project for project viewings (affects toast, tracking, revalidation)
   * @returns A promise that resolves to an object with success status and optimistic update info
   */
  const removeFromViewing = async (
    home: NestedHome | null,
    viewing: NestedViewing,
    project?: NestedProject
  ) => {
    if (!user) return { success: false };

    // Find the slot ID for this user's registration
    let viewingSlotId: number | null = null;
    if (viewing.viewing_slots && Array.isArray(viewing.viewing_slots)) {
      for (const slot of viewing.viewing_slots) {
        if (
          slot.viewing_participants &&
          Array.isArray(slot.viewing_participants)
        ) {
          const participant = slot.viewing_participants.find(
            (p) => p && p.user_id === user.id
          );
          if (participant) {
            viewingSlotId = slot.id;
            break;
          }
        }
      }
    }

    if (!viewingSlotId) {
      console.error('Could not find viewing slot for user');
      return { success: false };
    }

    // Prepare optimistic update info
    const optimisticUpdate = {
      viewing,
      isRegistered: false,
      user,
    };

    // Update local cache
    setViewingRegistrationCache((prev) => ({
      ...prev,
      [viewing.id]: false,
    }));

    try {
      const supabase = createClient();
      // Remove the user from the viewing participants
      await deleteViewingParticipant(supabase, user.id, viewingSlotId);
      // Duplicated logic follows
      // TODO: Move VITEC sync to a database table trigger and remove this line
      await deleteAPI(
        `/providers/supabase/viewing_participants/${user.id}/${viewingSlotId}`
      );

      // Revalidate the page cache to get fresh viewing data
      if (project) {
        await revalidateProjectViewing(project);
      } else if (home) {
        await revalidateHomeViewing(home);
      }

      // Refresh the user data to update the UI
      mutate(['users', user?.id]);

      // Refresh the page to update all components immediately
      router?.refresh();

      // Track viewing unregistration event
      track(EVENTS.UNBOOKED_VIEWING, {
        viewingId: viewing.id,
        projectId: project?.id,
        userId: user.id,
        agencyId: agency?.id,
      });

      // Show success toast
      const message = project
        ? getProjectViewingToastMessage(project, true)
        : home
        ? getViewingToastMessage(home, true)
        : { title: 'Visning avbokad', description: '' };
      showToast(message, {
        icon: <Icon as={ICONS.CALENDAR} boxSize={6} />,
      });

      return { success: true, optimisticUpdate };
    } catch (error) {
      console.error('Error removing from viewing:', error);
      return { success: false, optimisticUpdate };
    }
  };

  /**
   * Check if the current user is registered for a specific viewing object
   * @param viewing The viewing object to check
   * @returns True if the user is registered, false otherwise
   */
  const isUserRegisteredForViewing = useCallback(
    (viewing: NestedViewing): boolean => {
      if (!user || !viewing) return false;

      // First check local cache for the most up-to-date state
      if (viewing.id in viewingRegistrationCache) {
        return viewingRegistrationCache[viewing.id];
      }

      // Check if the user is registered in any slot's participants
      if (viewing.viewing_slots && Array.isArray(viewing.viewing_slots)) {
        const isRegistered = viewing.viewing_slots.some((slot) => {
          if (
            !slot.viewing_participants ||
            !Array.isArray(slot.viewing_participants)
          ) {
            return false;
          }
          return slot.viewing_participants.some(
            (participant: ViewingParticipant) => {
              return participant && participant.user_id === user.id;
            }
          );
        });

        return isRegistered;
      }

      // Fall back to false if participants data is not available
      return false;
    },
    [user, viewingRegistrationCache]
  );

  /**
   * Clear the local cache for a specific viewing or all viewings
   * @param viewingId Optional ID of the viewing to clear from cache
   */
  const clearViewingCache = useCallback((viewingId?: number) => {
    if (viewingId) {
      setViewingRegistrationCache((prev) => {
        const newCache = { ...prev };
        delete newCache[viewingId];
        return newCache;
      });
    } else {
      setViewingRegistrationCache({});
    }
  }, []);

  return {
    addToViewing,
    removeFromViewing,
    isUserRegisteredForViewing,
    clearViewingCache,
    viewingRegistrationCache,
    updateViewingCache: setViewingRegistrationCache,
  };
};
